import clsx from 'clsx';
import {
  type PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import {
  ToastContainer as ReactToastifyContainer,
  cssTransition,
  toast,
} from 'react-toastify';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { XmarkIcon } from '@dynatrace/strato-icons';

import { toastsCSS } from './ToastContainer.sty.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { createStore } from '../../core/store/store.js';
import { useKeyboardFocusHandling } from '../../overlays/hooks/useKeyboardFocusHandling.js';

type ToastContainerProps = PropsWithChildren<unknown> &
  StylingProps &
  DataTestId &
  MaskingProps;

let unique = 0;

const toastContainerStore = createStore<{ currentContainerId: number | null }>({
  currentContainerId: null,
});

/**
 * The `Toast` component provides a way to show live, time-sensitive feedback to users,
 * which includes updates or changes to the system status. To display the `Toast` component
 * use the `ToastContainer` along with the `showToast` function. The function accepts
 * `ToastOptions` as an argument, allowing you to pass various configurations, and returns
 * the corresponding toast id.
 * @public
 */
export const ToastContainer = forwardRef<HTMLDivElement, ToastContainerProps>(
  (props, forwardedRef) => {
    const {
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
    } = props;
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [handleFocusOnKeydown, closedOnKeydown] = useKeyboardFocusHandling(
      closeButtonRef,
      containerRef,
    );

    const rootRef = useMergeRefs([forwardedRef, containerRef]);

    const CloseButton = ({
      closeToast,
    }: {
      closeToast: (event: ReactMouseEvent<HTMLElement>) => void;
    }) => {
      const intl = useIntl();

      const closeButtonLabel = intl.formatMessage({
        defaultMessage: 'Close notification',
        id: '7IzOa/lx4Dc/qLGo',
        description: 'Aria-label for the close notification button.',
      });

      // Handle the escape key
      const closeToastOnEsc = (event: ReactKeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closedOnKeydown.current = true;
          // Limited typing on the end of the react-toastify library
          // requires a `React.MouseEvent` here, but they never actually
          // use anything very specific to the MouseEvent.
          closeToast(event as unknown as ReactMouseEvent<HTMLElement>);
        }
      };

      /** Only when the toast is closed by the keyboard we need to handle the focus */
      const detectCloseOnKeyDown = useCallback(() => {
        closedOnKeydown.current = true;
      }, []);

      return (
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          paddingTop={6}
          paddingRight={6}
          onKeyDown={closeToastOnEsc}
        >
          <Button
            onKeyDown={detectCloseOnKeyDown}
            onClick={closeToast}
            aria-label={closeButtonLabel}
            ref={closeButtonRef}
            variant="default"
            onPointerDown={(e: ReactMouseEvent) => e.stopPropagation()}
          >
            <Button.Prefix>
              <XmarkIcon />
            </Button.Prefix>
          </Button>
        </Flex>
      );
    };

    const containerId = useRef(++unique);

    const activeId = useSyncExternalStoreWithSelector(
      toastContainerStore.subscribe,
      () => toastContainerStore.getState(),
      null,
      (state) => state.currentContainerId,
    );

    useEffect(() => {
      const id = containerId.current;
      if (toastContainerStore.getState().currentContainerId === null) {
        toastContainerStore.setState({ currentContainerId: id });
      }
    }, [activeId]);

    useEffect(() => {
      const id = containerId.current;
      const unsubscribeOnChange = toast.onChange((payload) => {
        if (payload.status === 'removed') {
          // Need to wait for one tick here, otherwise the now removed toast is still
          // in the DOM and can be focused.
          window.requestAnimationFrame(() => {
            handleFocusOnKeydown();
          });
        }
      });

      return function cleanup() {
        unsubscribeOnChange();

        if (toastContainerStore.getState().currentContainerId === id) {
          toastContainerStore.setState({ currentContainerId: null });
        }
      };
      // excluded handleFocusOnKeydown dependency, because this effect needs to be executed once for toast.onChange
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (activeId !== containerId.current) {
      return null;
    }

    return createPortal(
      <div
        ref={rootRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={clsx(consumerClassName, toastsCSS)}
        style={consumerStyle}
        aria-live="polite"
        aria-atomic="false"
        //this data attribute is necessary to ensure the correct stacking order with the Microguide and FeatureHighlight
        data-toast-container
        aria-relevant="additions text"
      >
        <ReactToastifyContainer
          hideProgressBar={true}
          draggable={false}
          closeButton={CloseButton}
          limit={5}
          pauseOnHover={true}
          closeOnClick={false}
          newestOnTop={false}
          transition={cssTransition({
            collapseDuration: 200,
            enter: 'strato__toast__enter',
            exit: 'strato__toast__exit',
          })}
        />
      </div>,
      document.body,
    );
  },
);

(
  ToastContainer as typeof ToastContainer & { displayName: string }
).displayName = 'ToastContainer';
